import lottie from 'lottie-web';
import { loadFonts } from '@loaders/FontLoader';
import { config } from '@js/state';
import { ROUND_BOX_KEYS } from '@utils/roundGenerator';

/**
 * Asset manifest. Single background per scene; everything else is an
 * overlay. Backgrounds bake in the static scenery (sky, road, warehouses,
 * trees, parking platform, HUD frame, etc.) so the code never draws those.
 *
 * Box sprites are loaded dynamically from the per-round key list — each
 * round's 5 boxes are pre-rendered PNGs with the multiplication expression
 * baked in (e.g. box-r1-3x4.png).
 */
const GAME_ASSETS = [
  ['bg-out', 'background-1.png'],   // approach + dock + load + depart view
  ['bg-out-leaving', 'background-3.png'], // post-delivery view (truck driving away)
  ['bg-in', 'background-2.png'],
  ['bg-start', 'background-start.png'],   // StartScene title-screen warehouse
  ['truck', 'truck.png'],
  ['belt', 'belt.png'],
  ['board', 'board.png'],
  ['board-paper', 'board-paper.png'],
  ['left-button', 'left-button.png'],
  ['right-button', 'right-button.png'],
  ['parked-on', 'parked-enabled.png'],
  ['parked-off', 'parked-faded.png'],
  ['bulb-green', 'bulb-green.png'],
  ['bulb-red', 'bulb-red.png'],
  // 15 per-round box sprites (text baked in)
  ...ROUND_BOX_KEYS.map(key => [key, `${key}.png`]),
];

// Shared UI assets (CTA buttons + reward icons).
// Each CTA has a normal (yellow) and hover (dark) sprite — text is baked in.
const UI_ASSETS = [
  ['btn-play', 'btn-play.png'],
  ['btn-play-hover', 'btn-play-hover.png'],
  ['btn-play-again', 'btn-play-again.png'],
  ['btn-play-again-hover', 'btn-play-again-hover.png'],
  ['btn-submit', 'btn-submit.png'],
  ['btn-submit-hover', 'btn-submit-hover.png'],
  ['gold-crate', 'gold-crate.png'],
  ['spotlight-cone', 'spotlight-cone.png'],
  ['blinker', 'blinker.png'],
  ['sparkle', 'sparkle.png'],
  ['sparkle-large', 'sparkle-large.png'],
  ['star', 'star.png'],
  ['star-empty', 'star-empty.png'],
  ['ribbon', 'ribbon.png'],
  ['glass-tray', 'glass-tray.png'],
];

// EndScene assets — copied from games/buzzer-frizer for the spotlight +
// reward-reveal sequence (overlay, base circle, reward light, glitter
// orbit). Only the reward asset itself differs (truck-loading uses
// gold-crate where buzzer-frizer used compass/medal).
const END_SCENE_ASSETS = [
  ['end-overlay', 'overlay.png'],
  ['rewards-earned', 'rewards-earned.png'],
  ['reward-bg', 'reward-bg.png'],   // Figma 187:84 — sky+road backdrop
];

export default class LoadScene extends Phaser.Scene {
  constructor() {
    super({ key: 'LoadScene' });
  }

  async preload() {
    // Queue the Lottie JSON BEFORE awaiting fonts. Phaser auto-starts the
    // load queue at the end of preload() — by queueing first, the JSON is
    // in the cache by the time create() runs.
    this.load.json('loading-anim', 'assets/loadAnims/cuemath_loading.json');
    await loadFonts();
  }

  create() {
    this.cameras.main.setBackgroundColor('#FFFFFF');

    // Build the Lottie loader as a DOM child of the Phaser canvas. We track
    // both the Phaser DOM GameObject and the lottie anim handle so we can
    // tear both down before transitioning — leaving the DOM <div> alive
    // would let it intercept pointer events on the next scene.
    this.loader = this.buildLottieLoader();

    this.load.on('loaderror', file => {
      console.warn('[LoadScene] missing asset:', file.key, file.src);
    });

    if (this.loader && this.loader.anim) {
      this.loader.anim.playSegments([0, 25], true);
    }

    GAME_ASSETS.forEach(([key, file]) => {
      this.load.image(key, `assets/GameSceneAssets/${file}`);
    });

    UI_ASSETS.forEach(([key, file]) => {
      this.load.image(key, `assets/uiAssets/${file}`);
    });

    END_SCENE_ASSETS.forEach(([key, file]) => {
      this.load.image(key, `assets/EndSceneAssets/${file}`);
    });

    // Reward-reveal multiatlases (spotlight cone, base circle, reward light,
    // glitter). Sourced from buzzer-frizer's polished EndScene flow.
    this.load.multiatlas(
      'endSpotlight',
      'assets/EndSceneAssets/spotlight/spotlightAnimNew.json',
      'assets/EndSceneAssets/spotlight',
    );
    this.load.multiatlas(
      'endConfetti',
      'assets/EndSceneAssets/confetti/confettiAnimNew.json',
      'assets/EndSceneAssets/confetti',
    );
    this.load.multiatlas(
      'baseCircle',
      'assets/anims/rewardAnims/baseCircleAnim.json',
      'assets/anims/rewardAnims',
    );
    this.load.multiatlas(
      'rewardLight',
      'assets/anims/rewardAnims/rewardLightAnim.json',
      'assets/anims/rewardAnims',
    );
    this.load.multiatlas(
      'glitter',
      'assets/anims/rewardAnims/glitterAnim.json',
      'assets/anims/rewardAnims',
    );

    // Truck idle animations — replace the static truck image
    this.load.multiatlas(
      'truck-initial',
      'assets/truckAnims/truckInitialAnim.json',
      'assets/truckAnims',
    );
    this.load.multiatlas(
      'truck-final',
      'assets/truckAnims/truckFinalAnim.json',
      'assets/truckAnims',
    );
    // Idle/still loop used while the empty truck waits at the start of each
    // round (replaces the older truck-idle-empty visuals).
    this.load.multiatlas(
      'truck-still',
      'assets/truckAnims/truckStillAnim.json',
      'assets/truckAnims',
    );
    this.load.multiatlas(
      'truck-window',
      'assets/truckAnims/windowAnim.json',
      'assets/truckAnims',
    );

    // ─── Audio ────────────────────────────────────────────────────────
    // SFX palette + looping background music. The imported mp3s are the
    // primary cues; clunk/fanfare keep their wav source (no mp3 supplied).
    // Keys map to game events in GameScene (see playSfx/startLoop calls).
    const AUDIO_ASSETS = [
      ['bg-music', 'edugamery-music-bg.mp3'],        // looping background bed
      ['sfx-engine', 'truck-engine.mp3'],            // truck engine while driving (loop)
      ['sfx-conveyor', 'conveyor-belt.mp3'],         // conveyor belt while crates ride in (loop)
      ['sfx-whoosh', 'simple-whoosh-crates.mp3'],    // crate flies onto the truck
      ['sfx-clunk', 'clunk.wav'],                    // crate lands on the truck bed
      ['sfx-correct', 'correct-answer.mp3'],         // round solved (all correct crates loaded)
      ['sfx-wrong', 'wronganswer.mp3'],              // wrong crate dropped on the truck
      ['sfx-park-ding', 'cars-ding-for-parking.mp3'],// truck docks / parks
      ['sfx-fanfare', 'fanfare.wav'],                // win — plays on the reward reveal
    ];
    AUDIO_ASSETS.forEach(([key, file]) => {
      this.load.audio(key, `assets/audio/${file}`);
    });

    this.load.start();

    this.load.on('complete', () => {
      const moveOn = () => {
        this.teardownLoader();
        this.scene.stop('LoadScene');
        this.scene.start('StartScene');
      };

      if (this.loader && this.loader.anim) {
        // Play the closing segment, then tear everything down on its
        // complete event. The closing segment is short (~0.5s).
        this.loader.anim.loop = false;
        this.loader.anim.playSegments([26, 41], true);
        this.loader.anim.addEventListener('complete', moveOn);
        // Safety net: if Lottie never fires complete, transition anyway.
        this.time.delayedCall(1500, () => {
          if (this.loader) moveOn();
        });
      } else {
        moveOn();
      }
    });
  }

  // ─── Lottie helpers ───────────────────────────────────────────────────
  buildLottieLoader() {
    if (!this.cache.json.exists('loading-anim')) {
      console.warn('[LoadScene] cuemath_loading.json missing from cache');
      return null;
    }

    const cx = this.cameras.main.width / 2;
    const cy = this.cameras.main.height / 2;

    // Create the Phaser DOM wrapper at scene-centre. DOMElement bounds
    // come from the inner element — we set the inner div's size and CSS
    // transform-origin so the 0.7 scale stays centred over (cx, cy).
    const W_BOX = 400;
    const H_BOX = 400;
    const dom = this.add.dom(cx, cy, 'div').setOrigin(0.5).setScale(0.7);
    const node = dom.node;
    node.style.width = `${W_BOX}px`;
    node.style.height = `${H_BOX}px`;
    node.style.marginLeft = `${-W_BOX / 2}px`;
    node.style.marginTop = `${-H_BOX / 2}px`;
    node.style.pointerEvents = 'none';

    const anim = lottie.loadAnimation({
      container: node,
      renderer: 'svg',
      loop: true,
      autoplay: false,
      animationData: this.cache.json.get('loading-anim'),
    });

    return { dom, anim };
  }

  teardownLoader() {
    // Stop and destroy the Lottie animation, then drop its DOM container so
    // the SVG doesn't linger on the page or eat pointer events on the next
    // scene.
    if (!this.loader) return;
    try {
      if (this.loader.anim) {
        this.loader.anim.stop();
        this.loader.anim.destroy();
      }
      if (this.loader.dom && this.loader.dom.node) {
        this.loader.dom.node.innerHTML = '';
      }
      if (this.loader.dom) this.loader.dom.destroy();
    } catch (_e) {
      // best-effort cleanup; ignore
    }
    this.loader = null;
  }

  update() {}
}
